import { Component, ChangeDetectorRef, AfterViewInit, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SolicitudService } from '@services/solicitud.service';
import { CierreAdministrativoService } from '@services/cierre-administrativo.service';
import { SessionService } from '@services/session.service';
import { AlertService } from '@services/alert.service';
import { ExpedienteListItem, ExpedienteDetalle, InstitucionDetalle, DocumentoExpediente } from '@models/evaluacion-medica.interface';
import { DocumentoService } from '@services/documento.service';

interface TrazabilidadEvento {
  fecha: string; // ISO
  usuario: string;
  tipo: string; 
  descripcion: string;
}

@Component({
  selector: 'app-cierre-administrativo',
  templateUrl: './cierre-administrativo.component.html',
  styleUrls: ['./cierre-administrativo.component.scss']
})
export class CierreAdministrativoComponent implements OnInit {

  public id?: string | null;
  public expediente: ExpedienteDetalle | null = null;
  public institucion: InstitucionDetalle | null = null;
  public documentos: Array<{ name: string; url?: string | undefined; type: 'pdf' | 'zip' }> = [];
  public eventos: TrazabilidadEvento[] = [];
  
  public isLoading = false;
  public isProcessing = false;

  public motivosCierre = [
    'No cumple con el plazo mínimo de 180 días en los últimos dos años',
    'Falleció el funcionario',
    'Término del vínculo laboral',
    'La Institución no subsana observaciones',
    'Expediente sin movimiento en seis meses, sólo se notifica a la Institución',
    'La Institución desiste del trámite'
  ];

  public form: FormGroup;
  private readonly PLACEHOLDER_PDF = 'https://s2.q4cdn.com/175719177/files/doc_presentations/Placeholder-PDF.pdf';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private solicitudService: SolicitudService,
    private cierreAdminService: CierreAdministrativoService,
    private sessionService: SessionService,
    private alertService: AlertService,
    private documentoService: DocumentoService
  ) {
    this.form = this.fb.group({
      motivo: ['', Validators.required],
      observaciones: ['']
    });
  }

  ngOnInit(): void {
    this.id = this.route.snapshot.paramMap.get('id');
    if (this.id) {
      this.cargarDetalle(+this.id);
    }
  }

  async cargarDetalle(id: number) {
    this.isLoading = true;
    try {
       const expedienteCompleto = await this.solicitudService.obtenerExpedienteDetalle(id);
       
       if (expedienteCompleto) {
         this.expediente = expedienteCompleto.expediente;
         this.institucion = expedienteCompleto.institucion as unknown as InstitucionDetalle;
         
         if (expedienteCompleto.documentos && expedienteCompleto.documentos.length > 0) {
             this.documentos = expedienteCompleto.documentos.map((d: DocumentoExpediente) => ({
                 name: d.doc_nombre_archivo,
                 url: d.doc_url_segura,
                 type: d.doc_formato.toLowerCase() as 'pdf' | 'zip'
             }));
         }
       }
       
       // Load timeline events similarly to CasoDictamen if needed, or skip for now.
       // User said "vista similar", so let's try to get minimal detail.
       
    } catch (error) {
       console.error('Error al cargar detalle expediente', error);
       this.alertService.error('Error al cargar detalle del expediente');
    } finally {
       this.isLoading = false;
    }
  }

  async onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { motivo, observaciones } = this.form.value;

    if (motivo === 'La Institución desiste del trámite' && !observaciones.trim()) {
        this.alertService.warning('Para el motivo seleccionado, debe ingresar observaciones.');
        return;
    }
    
    // Confirm logic
    const confirmed = await this.alertService.confirm(
       'Confirmar Cierre', 
       'Esta acción generará un informe de cierre y no se podrá revertir. ¿Desea continuar?'
    );

    if (!confirmed) return;

    this.isProcessing = true;
    this.alertService.showLoading('Procesando cierre administrativo...');

    try {
        const sesion = this.sessionService.getSesionActual();
        const rutUsuario = sesion ? parseInt(sesion.rut_usuario.replace(/\D/g, ''), 10) : 1; 

        if (!this.expediente) {
             throw new Error('Expediente no cargado');
        }

        await this.cierreAdminService.realizarCierre({
            exp_id: this.expediente.exp_id,
            usu_rut: rutUsuario,
            motivo_cierre: motivo,
            observaciones_cierre: observaciones
        });

        this.alertService.success('Cierre administrativo realizado con éxito.');
        this.router.navigate(['/administracion/dictamen']);

    } catch (error) {
        console.error('Error en cierre administrativo', error);
        this.alertService.error('Error al realizar el cierre administrativo');
    } finally {
        this.alertService.close();
        this.isProcessing = false;
    }
  }
  
  async viewDocumento(doc: { name: string; url?: string | undefined; type: 'pdf' | 'zip' }) {
    if (!doc.url) {
        window.open(this.PLACEHOLDER_PDF, '_blank');
        return;
    }
    
    if (doc.url === this.PLACEHOLDER_PDF) {
         window.open(doc.url, '_blank');
         return;
    }

    try {
        const blob = await this.documentoService.verDocumento(doc.url);
        const objectUrl = window.URL.createObjectURL(blob);
        
        if (doc.type === 'pdf') {
            window.open(objectUrl, '_blank');
        } else {
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = doc.name;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    } catch (error) {
        this.alertService.error('Error al visualizar el documento');
    }
  }
}
